import React, { useState, useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaEnvelopeOpenText, FaSearch, FaTimes } from 'react-icons/fa';
import ReactTooltip from 'react-tooltip';
import api from '~/services/api';
import dates from '~/utils/dates';
import './styles.css';
import './Form/MailerSendingBanner/styles.css';

// Contexts
import { GeneralContext, ModalContext, PaginationContext } from '~/contexts';

// Components
import { normalizeMailerLogStatus } from '~/enums/mailersLogStatus';
import Admin from '~/components/Admin';
import { LoadingSingle } from '~/components/Default/Loading';
import LogBox from '~/components/Boxs/Mailers/Logs';
import {
    FormReport,
    FormFinancialGuaranteeAndBkpEnergy,
    FormCceeFees,
    FormCceeErcap,
    FormPreliminaryReport,
    FormNuclearEnergySettlement
} from './Form';

const NUCLEAR_MAILER_TYPE = 'NuclearEnergySettlement';

const AdminMailer: React.FC<any> = () => {
    const navigate                = useNavigate();
    const [ready, setReady]       = useState<Boolean>(false);
    const [logs, setLogs]         = useState<any[]>([]);
    const [search, setSearch]     = useState('');
    const [error, setError]       = useState<string | null>(null);
    const [selectedMailerAction, setSelectedMailerAction] = useState<string>('reportMailer');

    // Context
    const {user}     = useContext(GeneralContext);
    const Modal      = useContext(ModalContext);
    const Pagination = useContext(PaginationContext);

    const MAILER_ACTIONS: Array<{ value: string; label: string; tip: any; className: string }> = [
        { value: 'reportMailer', label: 'Relatórios', tip: 'Relatório', className: 'reportMailer' },
        { value: 'financialGuaranteeMailer', label: 'Garantia Financeira', tip: 'Garantia Financeira', className: 'financialGuaranteeMailer' },
        { value: 'bkpEnergyMailer', label: 'Energia de Reserva', tip: 'Energia de Reserva', className: 'bkpEnergyMailer' },
        { value: 'cceeFeesMailer', label: 'Taxa CCEE', tip: 'Taxa CCEE', className: 'cceeFeesMailer' },
        { value: 'cceeErcapMailer', label: 'Taxa ERCAP', tip: 'Taxa ERCAP', className: 'cceeErcapMailer' },
        { value: 'preliminaryReportMailer', label: 'Relatório Preliminar', tip: 'Relatório Preliminar', className: 'preliminaryReportMailer' },
        { value: 'nuclearEnergySettlementMailer', label: 'Liquidação Nuclear', tip: NUCLEAR_MAILER_TYPE, className: 'nuclearEnergySettlementMailer' },
    ];

    const selectedActionMeta =
        MAILER_ACTIONS.find((a) => a.value === selectedMailerAction) || MAILER_ACTIONS[0];

    function onChangeSearch (event: any) {
        let data = event.target.value;
        let logsFilter = logs.filter((el: any) =>
            el.typeName.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").indexOf(data.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, ""))>=0 ||
            (el.dateRef && el.dateRef.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").indexOf(data.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, ""))>=0)
            ? true : false
        )

        // Set Datas
        setSearch(data);
        Pagination.setCurrentPage(1);
        Pagination.setData(logsFilter);
    }

    function openModal(modal: any) {
        let openModal = false;

        // Define Modal
        Modal.setModalOpen(false);

        switch (modal) {
            case 'reportMailer':
                Modal.setModalClass('mreport');
                Modal.setModalTitle('Gerenciador de Envio de Relatórios');
                Modal.setModalBody(<FormReport setReadyPage={setReady} />)
                openModal = true;
                break;
            
            case 'preliminaryReportMailer':
                Modal.setModalClass('mreport');
                Modal.setModalTitle('Gerenciador de Envio de Relatórios Preliminares');
                Modal.setModalBody(<FormPreliminaryReport setReadyPage={setReady} />)
                openModal = true;
                break;

            case 'financialGuaranteeMailer':
                Modal.setModalClass('mfinancialguaranteebkpenergy');
                Modal.setModalTitle('Gerenciador de Envio de Garantia Financeira');
                Modal.setModalBody(<FormFinancialGuaranteeAndBkpEnergy type="financialGuarantee" setReadyPage={setReady} />)
                openModal = true;
                break;

            case 'bkpEnergyMailer':
                Modal.setModalClass('mbkpenergy');
                Modal.setModalTitle('Gerenciador de Envio de Energia de Reserva');
                Modal.setModalBody(<FormFinancialGuaranteeAndBkpEnergy type="bkpEnergy" setReadyPage={setReady} />)
                openModal = true;
                break;

            case 'cceeFeesMailer':
                Modal.setModalClass('mcceefees');
                Modal.setModalTitle('Gerenciador de Envio de Taxa CCEE');
                Modal.setModalBody(<FormCceeFees type="cceeFees" setReadyPage={setReady} />)
                openModal = true;
                break;

            case 'cceeErcapMailer':
                Modal.setModalClass('mcceeercap');
                Modal.setModalTitle('Gerenciador de Envio de Taxa ERCAP');
                Modal.setModalBody(<FormCceeErcap type="cceeErcap" setReadyPage={setReady} />)
                openModal = true;
                break;

            case 'nuclearEnergySettlementMailer':
                Modal.setModalClass('mnuclearenergysettlement');
                Modal.setModalTitle('Gerenciador de Envio - Liquidação de Energia Nuclear');
                Modal.setModalBody(<FormNuclearEnergySettlement setReadyPage={setReady} />)
                openModal = true;
                break;

            default:
                Modal.setModalClass(null);
                Modal.setModalTitle('');
                Modal.setModalBody(<></>);
                break;
        }

        // Open Modal
        if (openModal) Modal.setModalOpen(true)
    }

    function treatType (type: string) {
        switch (type) {
            case 'report':
                return 'Relatório de Economia';
            case 'financialGuarantee':
                    return 'Informativo de Garantia Financeira';
            case 'bkpEnergy':
                    return 'Informativo de Energia de Reserva';
            case 'cceeFees':
                    return 'Informativo da Taxa CCEE';
            case 'cceeErcap':
                    return 'Informativo da Taxa ERCAP';
            case 'preliminaryReport':
                    return 'Relatório Preliminar';
            case 'nuclearEnergySettlement':
                    return 'Liquidação de Energia Nuclear';
            default:
                return 'Outro';
        }
    }

    // Mantém a lógica original (carregar apenas quando `ready` for false),
    // mas deixa o dependency array completo para evitar warnings do React Hooks.
    useEffect(() => {
        // Check Permission
        if (user.role === 'client') navigate('/404', { replace: true })
        else if (!ready) {
            // Reset paginação apenas quando for recarregar
            Pagination.init();
            Pagination.setItemsPerPage(10);

            setError(null);

            api.get(`mailers/logs`, {headers: { Authorization: user.token }})
                .then((resp: any) => {
                    const list: any[] = Array.isArray(resp?.data) ? resp.data : [];

                    // Agrupa por hash de forma segura (não depende do retorno já vir ordenado)
                    const map = new Map<string, any>();

                    for (const el of list) {
                        const hash = String(el?.hash ?? '');
                        if (!hash) continue;

                        let item = map.get(hash);
                        if (!item) {
                            const type = String(el?.type ?? '');
                            const typeName = treatType(type);

                            // Treat Reference Date (el.ref pode vir null)
                            let dateRef = '';
                            try {
                                if (el?.ref) {
                                    const baseRef = dates.getDate(String(el.ref));
                                    dateRef = `${dates._getNameMonth(baseRef.month - 1)} de ${baseRef.year}`;
                                }
                            } catch (_) {
                                dateRef = '';
                            }

                            // Treat Send Date (el.date pode vir inválida)
                            let sendDate = '';
                            try {
                                if (el?.date) sendDate = dates.getDateFormatted(el.date, 'dd-mm-yyyy HH:MM');
                            } catch (_) {
                                sendDate = '';
                            }

                            item = {
                                hash,
                                type,
                                typeName,
                                dateRef,
                                date: sendDate,
                                user: el?.user?.name ?? '-',
                                data: []
                            };
                            map.set(hash, item);
                        }

                        item.data.push({
                            id: el?.id,
                            mail: el?.mail ?? '-',
                            unit: el?.unit?.name ?? '-',
                            status: normalizeMailerLogStatus(el?.status),
                            message: el?.message ?? ''
                        });
                    }

                    const data = Array.from(map.values());
                    Pagination.setData(data);
                    setLogs(data);
                })
                .catch((e: any) => {
                    // Evita ficar preso no loading em caso de erro
                    setError(e?.response?.data?.message || e?.message || 'Erro ao carregar logs.');
                    Pagination.setData([]);
                    setLogs([]);
                })
                .finally(() => {
                    setReady(true);
                });
        }
    }, [ready])

    return <Admin pageTitle="Gerenciamento de Emails - Clientes Serena" title="Gerenciamento" subtitle="Controle de Envio de Emails" classMain='adm_mailer'>
        {
            ready ? <>
                {error ? <span className="alert">{error}</span> : null}
                <div className="header">
                    <div className="buttons">
                        <select
                            className="mailerActionSelect"
                            value={selectedMailerAction}
                            onChange={(e: any) => setSelectedMailerAction(e.target.value)}
                            aria-label="Selecionar tipo de envio"
                        >
                            {MAILER_ACTIONS.map((a) => (
                                <option key={a.value} value={a.value}>
                                    {a.label}
                                </option>
                            ))}
                        </select>

                        <button
                            type="button"
                            className={selectedActionMeta.className}
                            onClick={() => openModal(selectedMailerAction)}
                            data-for="tooltip_mailer_page"
                            data-tip={selectedActionMeta.tip}
                        >
                            <FaEnvelopeOpenText size="16" />
                            <span>Abrir</span>
                        </button>
                    </div>

                    <div className='searchBox'>
                        <input
                            name="search" id="search"
                            type="text" autoComplete='false'
                            placeholder='Tipo de envio e data referência...'
                            value={search} onChange={onChangeSearch}
                            disabled={ready ? false : true}
                        />

                        <div className={`btn${search !=='' ? ' lnk' : ''}`} onClick={() => {
                            if (search !=='') {
                                Pagination.setCurrentPage(1)
                                Pagination.setData(logs)
                                setSearch('')
                            }
                        }}>
                            { search !=='' ? <FaTimes /> : <FaSearch /> }
                        </div>
                    </div>
                </div>

                <div className="logs">
                    {
                        Pagination.currentData.length ? Pagination.currentData.map((el: any) => (
                            el.hash && <LogBox
                                key={`log_${el.hash}`}
                                {...el}
                            />
                        )) : <span className="alert">Não há logs registrados no momento</span>
                    }
                </div>

                { Pagination.renderPagination() }

                <ReactTooltip
                    id="tooltip_mailer_page"
                    place="top"
                    effect="solid"
                    type="info"
                />
            </> : <LoadingSingle />
        }
    </Admin>
}

export default AdminMailer;